import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateExternalPlanDto,
  QueryExternalPlansDto,
  UpdateExternalPlanDto,
} from '@control-center/shared';

import {
  createExternalPlan,
  deleteExternalPlan,
  fetchExternalPlan,
  fetchExternalPlans,
  updateExternalPlan,
  type ExternalPlanEntity,
} from '../lib/external-plans-api';

export const externalPlansKeys = {
  all: ['external-plans'] as const,
  lists: () => [...externalPlansKeys.all, 'list'] as const,
  list: (filters: QueryExternalPlansDto) => [...externalPlansKeys.lists(), filters] as const,
  detail: (id: string) => [...externalPlansKeys.all, 'detail', id] as const,
};

export function useExternalPlans(filters: QueryExternalPlansDto = { limit: 50 }) {
  return useQuery({
    queryKey: externalPlansKeys.list(filters),
    queryFn: () => fetchExternalPlans(filters),
    staleTime: 30_000,
  });
}

export function useExternalPlan(id: string | undefined) {
  return useQuery<ExternalPlanEntity>({
    queryKey: externalPlansKeys.detail(id ?? ''),
    queryFn: () => fetchExternalPlan(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateExternalPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateExternalPlanDto) => createExternalPlan(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: externalPlansKeys.all });
    },
  });
}

export function useUpdateExternalPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateExternalPlanDto }) =>
      updateExternalPlan(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: externalPlansKeys.all });
      qc.invalidateQueries({ queryKey: externalPlansKeys.detail(vars.id) });
    },
  });
}

export function useDeleteExternalPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteExternalPlan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: externalPlansKeys.all });
    },
  });
}
